import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Filter, RefreshCw, TrendingUp, Users, CalendarDays, CreditCard } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminMRR from "@/components/AdminMRR";

const PERIODS = [
  { v: "all", l: "Tudo" }, { v: "hoje", l: "Hoje" }, { v: "semana", l: "Semana" },
  { v: "mes", l: "Mês" }, { v: "ano", l: "Ano" }, { v: "custom", l: "Personalizado" },
];
const FIN_CARDS = [
  ["Total processado", "total_processado", "text-accent"],
  ["Pago pelos organizadores", "total_pago_organizadores", "text-zinc-100"],
  ["Destinado aos músicos", "total_destinado_musicos", "text-zinc-100"],
  ["Repassado aos músicos", "total_repassado_musicos", "text-emerald-400"],
  ["Aguardando repasse", "aguardando_repasse", "text-amber-400"],
  ["Pagamento pendente", "pagamento_pendente", "text-amber-400"],
  ["Falha de pagamento", "falha_pagamento", "text-red-400"],
  ["Falha de repasse", "falha_repasse", "text-red-400"],
  ["Taxa do gateway", "taxa_gateway", "text-zinc-400"],
  ["Taxa Mí Chamou", "taxa_plataforma", "text-[#FF4D00]"],
  ["Receita bruta", "receita_bruta", "text-zinc-100"],
  ["Receita líquida", "receita_liquida", "text-emerald-400"],
  ["Estornos", "estornos", "text-zinc-400"],
  ["Ticket médio / evento", "ticket_medio_evento", "text-zinc-100"],
  ["Ticket médio / músico", "ticket_medio_musico", "text-zinc-100"],
];
const STATUS_OPTS = ["PENDING", "PROCESSING", "PAID", "EXPIRED", "CANCELLED", "FAILED", "REFUNDED", "PARTIALLY_REFUNDED"];
const STATUS_PT = { PENDING: "Aguardando", PROCESSING: "Processando", PAID: "Pago", EXPIRED: "Expirado", CANCELLED: "Cancelado", FAILED: "Falhou", REFUNDED: "Estornado", PARTIALLY_REFUNDED: "Estorno parcial" };

export default function AdminReports() {
  const { user } = useAuth();
  const canFinance = user?.permissions?.includes("finance:read");
  const [period, setPeriod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [ids, setIds] = useState({ organizer_id: "", musician_id: "", event_id: "", payment_id: "", payout_id: "" });
  const [fin, setFin] = useState(null);
  const [oper, setOper] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useCallback(() => {
    const p = { period };
    if (period === "custom") { if (dateFrom) p.date_from = dateFrom; if (dateTo) p.date_to = dateTo; }
    if (status) p.status = status;
    Object.entries(ids).forEach(([k, v]) => { if (v.trim()) p[k] = v.trim(); });
    return p;
  }, [period, dateFrom, dateTo, status, ids]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/admin/reports/operational", { params: { period, date_from: dateFrom || undefined, date_to: dateTo || undefined } })];
      if (canFinance) reqs.unshift(api.get("/admin/reports/financial", { params: params() }));
      const res = await Promise.all(reqs);
      if (canFinance) { setFin(res[0].data); setOper(res[1].data); }
      else setOper(res[0].data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [params, period, dateFrom, dateTo, canFinance]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (fmt) => {
    try {
      const res = await api.get("/admin/reports/financial", { params: { ...params(), export: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `relatorio_financeiro.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Relatório exportado (${fmt.toUpperCase()}).`);
    } catch { toast.error(`Não foi possível exportar (${fmt.toUpperCase()}).`); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Filtros */}
      <div className="bg-card border border-white/[0.06] rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => (
            <button key={p.v} data-testid={`period-${p.v}`} onClick={() => setPeriod(p.v)}
              className={`px-3 py-1.5 rounded-xl text-sm border transition-colors ${period === p.v ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/10 text-zinc-400 hover:border-white/25"}`}>{p.l}</button>
          ))}
          <div className="flex-1" />
          <Button data-testid="reload-reports-btn" onClick={load} variant="outline" size="sm" className="rounded-xl bg-transparent border-white/10 gap-1"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Atualizar</Button>
          <button data-testid="toggle-advanced" onClick={() => setAdvanced((a) => !a)} className="text-xs text-accent flex items-center gap-1 hover:underline"><Filter className="h-3.5 w-3.5" />Filtros avançados</button>
        </div>
        {period === "custom" && (
          <div className="flex flex-wrap gap-2 items-end">
            <div><label className="text-xs text-zinc-500">De</label><Input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-[#09090B] border-white/10 rounded-xl" /></div>
            <div><label className="text-xs text-zinc-500">Até</label><Input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-[#09090B] border-white/10 rounded-xl" /></div>
          </div>
        )}
        {advanced && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-2 border-t border-white/[0.06]">
            <div>
              <label className="text-xs text-zinc-500">Status do pagamento</label>
              <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="status-filter" className="bg-[#09090B] border-white/10 rounded-xl h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="all">Todos</SelectItem>
                  {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{STATUS_PT[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {[["organizer_id", "ID organizador"], ["musician_id", "ID músico"], ["event_id", "ID evento"], ["payment_id", "ID pagamento (gateway)"], ["payout_id", "ID repasse"]].map(([k, l]) => (
              <div key={k}><label className="text-xs text-zinc-500">{l}</label><Input data-testid={`filter-${k}`} value={ids[k]} onChange={(e) => setIds((p) => ({ ...p, [k]: e.target.value }))} placeholder="—" className="bg-[#09090B] border-white/10 rounded-xl h-9" /></div>
            ))}
          </div>
        )}
      </div>

      {/* Financeiro */}
      {canFinance && (
        <div className="space-y-3" data-testid="mrr-section">
          <h2 className="font-head text-xl font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-400" />MRR — Receita Recorrente Mensal</h2>
          <AdminMRR />
        </div>
      )}

      {/* Financeiro */}
      {canFinance && fin && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-head text-xl font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5 text-accent" />Financeiro <span className="text-sm text-zinc-500 font-normal">({fin.totals.qtd_transacoes} transações pagas)</span></h2>
            <div className="flex items-center gap-2">
              <Button data-testid="export-xlsx-btn" onClick={() => doExport("xlsx")} variant="outline" size="sm" className="rounded-xl bg-transparent border-emerald-500/30 text-emerald-400 gap-1"><Download className="h-4 w-4" />Excel</Button>
              <Button data-testid="export-csv-btn" onClick={() => doExport("csv")} variant="outline" size="sm" className="rounded-xl bg-transparent border-white/10 gap-1"><Download className="h-4 w-4" />CSV</Button>
              <Button data-testid="export-pdf-btn" onClick={() => doExport("pdf")} variant="outline" size="sm" className="rounded-xl bg-transparent border-red-500/30 text-red-400 gap-1"><Download className="h-4 w-4" />PDF</Button>
            </div>
          </div>
          {!fin.gateway_fee_available && (
            <p className="text-[11px] text-amber-400/80">A taxa do gateway (Asaas) por transação ainda não foi capturada — será preenchida ao abrir o detalhe da transação (rastreio Pix). Receita líquida ainda não desconta a taxa do gateway.</p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {FIN_CARDS.map(([l, k, c]) => (
              <div key={k} data-testid={`fin-card-${k}`} className="bg-card border border-white/[0.06] rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
                <div className={`mono text-lg font-bold ${c}`}>{money(fin.totals[k])}</div>
              </div>
            ))}
          </div>

          {/* Tabela de transações */}
          <div className="bg-card border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/[0.06] flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" />Transações ({fin.orders.length})</div>
            <div className="max-h-[420px] overflow-auto divide-y divide-white/[0.04]">
              {fin.orders.length === 0 ? <p className="p-6 text-center text-sm text-zinc-500">Nenhuma transação no período/filtro.</p> :
                fin.orders.map((o) => (
                  <div key={o.id} data-testid={`tx-row-${o.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{o.event_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{o.organizer_name} · {o.provider} · {(o.created_at || "").slice(0, 10)}</div>
                    </div>
                    <span className="mono text-accent shrink-0">{money(o.total)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-xl border shrink-0 ${o.status === "PAID" ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : o.status === "FAILED" || o.status === "EXPIRED" ? "border-red-500/40 text-red-400 bg-red-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>{STATUS_PT[o.status] || o.status}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Operacional */}
      {oper && (
        <div className="space-y-3">
          <h2 className="font-head text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-[#FF4D00]" />Operacional</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <OperBlock title="Usuários" icon={Users} data={[["Total", oper.usuarios.total], ["Ativos", oper.usuarios.ativos], ["Inativos", oper.usuarios.inativos], ["Músicos", oper.usuarios.musicos], ["Organizadores", oper.usuarios.organizadores], ["Ambos", oper.usuarios.ambos]]} />
            <OperBlock title="Eventos" icon={CalendarDays} data={[["Criados", oper.eventos.criados], ["Abertos", oper.eventos.abertos], ["Concluídos", oper.eventos.concluidos], ["Cancelados", oper.eventos.cancelados]]} />
            <OperBlock title="Candidaturas" icon={Users} data={[["Total", oper.candidaturas.total], ["Aprovados", oper.candidaturas.aprovados], ["Check-ins", oper.candidaturas.check_ins], ["Check-outs", oper.candidaturas.check_outs]]} />
            <OperBlock title="Pagamentos" icon={CreditCard} data={[["Efetuados", oper.pagamentos.efetuados], ["Pendentes", oper.pagamentos.pendentes], ["Com erro", oper.pagamentos.com_erro]]} />
          </div>
        </div>
      )}
    </div>
  );
}

const OperBlock = ({ title, icon: Icon, data }) => (
  <div data-testid={`oper-${title.toLowerCase()}`} className="bg-card border border-white/[0.06] rounded-xl p-4">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2"><Icon className="h-3.5 w-3.5" />{title}</div>
    <div className="space-y-1">
      {data.map(([l, v]) => (
        <div key={l} className="flex justify-between text-sm"><span className="text-zinc-400">{l}</span><span className="mono font-semibold">{v}</span></div>
      ))}
    </div>
  </div>
);
